/**
 * Centralized accessor for the app's `VITE_*` environment variables.
 *
 * Read env values from `env` here rather than from `import.meta.env.VITE_*`
 * inline. Keeps env keys discoverable and lets us add validation in one
 * place if we ever introduce required-at-boot vars.
 *
 * Each key resolves RUNTIME-first: `window.__ENV__` (written into `/env.js` by
 * the container's `docker-entrypoint.d/40-runtime-env.sh` from the pod's
 * `VITE_*` env — i.e. the ArgoCD manifest), then the build-time
 * `import.meta.env` value Vite baked in, then the default below. An empty
 * string at either layer counts as unset, so a blank manifest entry can't mask
 * a real build value and a blank build value still reaches the default.
 * Locally and in tests `public/env.js` is an empty object, so behaviour there
 * is unchanged.
 */

type EnvKey = keyof ImportMetaEnv & `VITE_${string}`;

function read(key: EnvKey): string | undefined {
  const runtime =
    typeof window === "undefined" ? undefined : window.__ENV__?.[key];
  if (runtime) return runtime;
  const built = import.meta.env[key];
  return typeof built === "string" && built !== "" ? built : undefined;
}

type AppEnv = "development" | "staging" | "production";

function readAppEnv(): AppEnv {
  const raw = read("VITE_APP_ENV");
  if (raw === "staging" || raw === "production") return raw;
  return "development";
}

/** Matches `@paddle/paddle-js`'s `Environments` union. */
type PaddleEnvironment = "sandbox" | "production";

function readPaddleEnvironment(): PaddleEnvironment {
  return read("VITE_PADDLE_ENVIRONMENT") === "production"
    ? "production"
    : "sandbox";
}

export const env = {
  apiUrl: read("VITE_API_URL") ?? "http://localhost:3000",
  appEnv: readAppEnv(),
  /**
   * Base URL of the public API documentation site (`apps/docs`). Settings →
   * API keys links out to it, so a key comes with the page explaining how to
   * send it. Defaults to the local docs dev server (port 3004, hardcoded in
   * that app's next config) — staging and production must set VITE_DOCS_URL to
   * the deployed docs host, or the link points at the reader's own machine.
   */
  docsUrl: read("VITE_DOCS_URL") ?? "http://localhost:3004",
  /**
   * Public Google OAuth client ID. Dual-purpose: displayed in the "Connect
   * your Google Account" admin-consent modal (Copy button no-ops when this
   * is falsy), and passed live to `google.accounts.id.initialize` for the
   * Login/Register page's One Tap prompt. Must match backend
   * `GOOGLE_CLIENT_ID` — the One Tap ID token's `aud` claim is verified
   * against it. Empty string fallback so both call sites no-op/placeholder
   * cleanly during local dev when the env var is unset.
   */
  gmailOAuthClientId: read("VITE_GMAIL_OAUTH_CLIENT_ID") ?? "",
  /**
   * Public service-account Client ID for Google domain-wide delegation (DWD
   * bulk workspace connect). Shown in the connect-google guide's "authorize in
   * the Admin Console" step + copy button. Empty-string fallback so the guide
   * renders with an "ask your admin" placeholder when the env var is unset
   * (the Copy button no-ops when this is falsy).
   */
  googleDwdServiceAccountClientId:
    read("VITE_GOOGLE_DWD_SA_CLIENT_ID") ?? "",
  posthogKey: read("VITE_POSTHOG_KEY") ?? "",
  posthogHost: read("VITE_POSTHOG_HOST") ?? "https://us.i.posthog.com",
  // Firebase client SDK config, backing the infra in `lib/firebase/`.
  firebaseApiKey: read("VITE_FIREBASE_API_KEY") ?? "",
  firebaseAuthDomain: read("VITE_FIREBASE_AUTH_DOMAIN") ?? "",
  firebaseProjectId: read("VITE_FIREBASE_PROJECT_ID") ?? "",
  firebaseStorageBucket: read("VITE_FIREBASE_STORAGE_BUCKET") ?? "",
  firebaseMessagingSenderId:
    read("VITE_FIREBASE_MESSAGING_SENDER_ID") ?? "",
  firebaseAppId: read("VITE_FIREBASE_APP_ID") ?? "",
  firebaseUserIdHashSalt: read("VITE_FIREBASE_USER_ID_HASH_SALT") ?? "",
  /**
   * Public Paddle.js client-side token (`live_…` / `test_…`). Drives the
   * self-serve checkout overlay. Empty-string fallback so local dev without
   * Paddle keys doesn't crash — `features/billing/paddle.ts` treats an empty
   * token as "not configured" and disables the upgrade buttons.
   */
  paddleClientToken: read("VITE_PADDLE_CLIENT_TOKEN") ?? "",
  /** Paddle environment for `initializePaddle`. Defaults to `sandbox`. */
  paddleEnvironment: readPaddleEnvironment(),
  /**
   * Public Gleap support-chat widget API key. Powers `lib/gleap.ts`'s
   * `initGleap`/`openGleapChat` — the "Chat with support" action on
   * `NeedHelpConnectingBanner` (mailbox-connection-failure help prompt).
   * Empty-string fallback so local dev without a key doesn't load the
   * third-party script; `initGleap` no-ops on an empty key.
   */
  gleapApiKey: read("VITE_GLEAP_API_KEY") ?? "",
  /**
   * Public BetterBugs project key. Powers `lib/betterbugs.ts` — the "Submit
   * Feedback" button in the sidebar (`Sidebar.tsx`).
   * Empty-string fallback disables the integration: no third-party script is
   * loaded and opening the widget shows a "not available" warning instead.
   */
  betterbugsApiKey: read("VITE_BETTERBUGS_API_KEY") ?? "",
  /**
   * HighAdvocacy popup widget (`lib/high-advocacy.ts`) — the review-rewards
   * modal on a mailbox's Overview page. Script URL + the TrulyInbox review
   * campaign. Either one empty disables the prompt entirely: no eligibility
   * request, no third-party script.
   */
  highAdvocacyWidgetUrl: read("VITE_HIGH_ADVOCACY_WIDGET_URL") ?? "",
  highAdvocacyCampaignId: read("VITE_HIGH_ADVOCACY_CAMPAIGN_ID") ?? "",
  /**
   * Public Cloudflare Turnstile site key. Renders the CAPTCHA widget on every
   * sign-in screen — login, register, forgot-password and the session-expired
   * modal (`features/auth/use-turnstile.ts`).
   * Pairs with the api's `CLOUDFLARE_TURNSTILE_SECRET_KEY` — set both or
   * neither. Empty-string fallback so local dev without a key renders no widget
   * and sends no token.
   */
  turnstileSiteKey: read("VITE_CLOUDFLARE_TURNSTILE_SITE_KEY") ?? "",
} as const;
